import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ModulePackagesService} from '@app/services/api/module-packages.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ModulePackage} from '@app/interfaces/packages/module-package';
import {AccountModulePackage} from '@app/interfaces/packages/account-module-package';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {ModulePackageActivationSyncService} from '@app/services/util/module-package-activation-sync.service';
import {IntegrationsNames} from '@app/enums/IntegrationTypeEnum';

export interface ModulePackageGroup {
  moduleType: string;
  displayName: string;
  packages: ModulePackage[];
}

@Component({
  selector: 'app-display-module-packages-page',
  templateUrl: './display-module-packages-page.component.html',
  styleUrls: ['./display-module-packages-page.component.scss'],
  standalone: false
})
export class DisplayModulePackagesPageComponent implements OnInit, OnDestroy {
  @Input() featureFlag: AccountFeaturesEnum = null;

  public availableModules: ModulePackage[] = [];
  public activeModules: AccountModulePackage[] = [];
  public moduleGroups: ModulePackageGroup[] = [];
  public isLoading: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private modulePackagesService: ModulePackagesService,
    private activationSync: ModulePackageActivationSyncService
  ) {
  }

  ngOnInit(): void {
    this.loadModules();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadModules(): void {
    this.isLoading = true;

    // Load available and active modules in parallel
    this.modulePackagesService.getAllModulePackages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.availableModules = result.data;

          // Filter by feature flag if provided
          if (this.featureFlag) {
            this.availableModules = this.availableModules.filter((modulePackage) => {
              return modulePackage.featureFlags?.[this.featureFlag] ?? false;
            });
          }

          // Filter out non-upsellable modules
          this.availableModules = this.availableModules.filter((modulePackage) => {
            return modulePackage.isUpsellable !== false;
          });

          this.loadActiveModules();
        },
        error: (error) => {
          console.error('Error loading available modules:', error);
          this.isLoading = false;
        }
      });
  }

  loadActiveModules(): void {
    this.modulePackagesService.getActiveModulePackages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.activeModules = result.data;
          this.moduleGroups = this.groupedModules();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading active modules:', error);
          this.isLoading = false;
        }
      });
  }

  isModuleActive(moduleUuid: string): boolean {
    return this.activeModules.some(active => active.modulePackageUuid === moduleUuid);
  }

  getActiveModule(moduleUuid: string): AccountModulePackage | undefined {
    return this.activeModules.find(active => active.modulePackageUuid === moduleUuid);
  }

  hasConflictingActiveModule(module: ModulePackage): boolean {
    if (!module.moduleType) {
      return false;
    }

    // Find modules in the same exclusion group
    const conflictingModules = this.availableModules.filter(
      m => m.moduleType === module.moduleType && m.uuid !== module.uuid
    );

    // Check if any of them is active
    return conflictingModules.some(m => this.isModuleActive(m.uuid));
  }

  getConflictingActiveModule(module: ModulePackage): ModulePackage | undefined {
    if (!module.moduleType) {
      return undefined;
    }

    const conflictingModules = this.availableModules.filter(
      m => m.moduleType === module.moduleType && m.uuid !== module.uuid
    );

    return conflictingModules.find(m => this.isModuleActive(m.uuid));
  }

  isUpgrade(module: ModulePackage): boolean {
    const conflictingModule = this.getConflictingActiveModule(module);
    if (!conflictingModule) {
      return false;
    }

    const moduleOrder = module.order ?? 0;
    const conflictOrder = conflictingModule.order ?? 0;

    return moduleOrder > conflictOrder;
  }

  isDowngrade(module: ModulePackage): boolean {
    const conflictingModule = this.getConflictingActiveModule(module);
    if (!conflictingModule) {
      return false;
    }

    const moduleOrder = module.order ?? 0;
    const conflictOrder = conflictingModule.order ?? 0;

    return moduleOrder < conflictOrder;
  }

  groupedModules(): ModulePackageGroup[] {
    const groups = new Map<string, ModulePackage[]>();

    for (const pkg of this.availableModules) {
      const key = pkg.moduleType ?? 'other';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(pkg);
    }

    return Array.from(groups.entries()).map(([moduleType, packages]) => ({
      moduleType,
      // ModulePackage.moduleType is stored using ModulePackageEnum's uppercase constants
      // (e.g. 'VIRTUAL_SALON'), while IntegrationsNames is keyed by IntegrationTypeEnum's
      // lowercase values (e.g. 'virtual_salon') — normalize case so the family-name lookup
      // actually matches instead of silently falling through to packages[0]?.name every time.
      displayName: (IntegrationsNames as Record<string, string>)[moduleType.toLowerCase()] ?? packages[0]?.name ?? moduleType,
      packages,
    }));
  }

  onModuleActivated(): void {
    this.activationSync.syncAfterChange();
    this.loadModules();
  }

  onModuleDeactivated(): void {
    this.activationSync.syncAfterChange();
    this.loadModules();
  }

  trackByModuleType(_index: number, group: ModulePackageGroup): string {
    return group.moduleType;
  }

  trackByUuid(_index: number, module: ModulePackage): string {
    return module.uuid;
  }
}
